import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ResourceSidebar from '../../../shared/ResourceSidebar';
import { useWorkerSidebarConflicts } from './hooks/useWorkerSidebarConflicts';

/**
 * WorkerSidebar Component (Refactored)
 * Uses the shared ResourceSidebar to provide a management interface for resources.
 * 
 * @param {Object} props - Component properties.
 */
export default function WorkerSidebar({
    workers, vehicles, assignments, absences, categories,
    onOpenWorkerModal,
    onOpenConflictOverview,
    onOpenKolonnenManagement,
    onConflictHover,
    onConflictHoverEnd,
    onDragStart,
    isEditMode = false,
    onAddResource = null,
    onRemoveResource = null,
    assignedWorkers = [],
    assignedVehicles = [],
    kolonnen = [],
    calculatedConflicts = [],
    interaction,
    setInteraction,
    sidebarMode = 'resources',
    onModeChange,
    isConflictDetectionEnabled = true,
    logicalConflictCount = 0
}) {
    const navigate = useNavigate();

    // Logic for conflicts and statuses (Leader specific)
    const {
        getWorkerConflicts,
        getVehicleConflicts,
        getKolonneConflicts,
        getWorkerStatus,
        getVehicleStatus
    } = useWorkerSidebarConflicts({
        workers,
        vehicles,
        assignments,
        absences,
        calculatedConflicts
    });

    // Compute assigned IDs for visual marking
    const assignedIds = useMemo(() => {
        const ids = [];
        assignedWorkers.forEach(aw => ids.push(String(aw.worker_id)));
        assignedVehicles.forEach(av => ids.push(String(av.vehicle_id)));
        return ids;
    }, [assignedWorkers, assignedVehicles]);

    /**
     * Unified toggle handler for resources and kolonnen
     */
    const handleToggle = (resource) => {
        const isKolonne = !!resource.members;
        const id = String(resource.id);
        
        if (isKolonne) {
            // Check if all members are assigned
            const memberWorkerIds = (resource.members || []).filter(m => m.type === 'worker').map(m => String(m.resource_id));
            const memberVehicleIds = (resource.members || []).filter(m => m.type === 'vehicle').map(m => String(m.resource_id));
            
            const isFullyAssigned = memberWorkerIds.every(mid => assignedWorkers.some(aw => String(aw.worker_id) === mid)) && 
                                   memberVehicleIds.every(mid => assignedVehicles.some(av => String(av.vehicle_id) === mid));

            if (isFullyAssigned) {
                // Remove all members
                resource.members.forEach(m => onRemoveResource && onRemoveResource({ type: m.type, id: m.resource_id }));
            } else {
                // Add all members
                resource.members.forEach(m => {
                    const res = m.type === 'worker' ? workers.find(w => w.id === m.resource_id) : vehicles.find(v => v.id === m.resource_id);
                    onAddResource && onAddResource({ 
                        type: m.type, 
                        id: m.resource_id, 
                        kolonne_id: resource.id,
                        name: res?.name || res?.license_plate || res?.internal_name || '',
                        role: res?.role || ''
                    });
                });
            }
        } else {
            const type = resource.license_plate ? 'vehicle' : 'worker';
            const isAssigned = assignedIds.includes(id);

            if (isAssigned) {
                onRemoveResource && onRemoveResource({ type, id: resource.id });
            } else {
                onAddResource && onAddResource({
                    type,
                    id: resource.id,
                    name: resource.name || resource.license_plate,
                    role: resource.role || '',
                    license_plate: resource.license_plate,
                    internal_name: resource.internal_name
                });
            }
        }
    };

    /**
     * Leader-specific Header Actions (Conflict Center)
     */
    const headerActions = (
        <button 
            className={`sidebar-conflict-center-btn ${isConflictDetectionEnabled && logicalConflictCount > 0 ? 'has-conflicts' : ''}`}
            onClick={() => onOpenConflictOverview && onOpenConflictOverview()}
        >
            <span className="sc-icon text-base">
                {!isConflictDetectionEnabled ? '⚪️' : logicalConflictCount > 0 ? '⚠️' : '✅'}
            </span>
            <span className="sc-label font-bold text-[13px] text-slate-800">
                Konflikt-Zentrale
            </span>
            {!isConflictDetectionEnabled ? (
                <span className="sc-badge-inline is-off">AUS</span>
            ) : logicalConflictCount > 0 ? (
                <span className="sc-badge-inline">{logicalConflictCount} Konflikte</span>
            ) : (
                <span className="sc-badge-inline is-ok">OK</span>
            )}
        </button>
    );

    return (
        <div className="resource-sidebar-container is-sidebar">
            {headerActions && (
                <div style={{ padding: '12px 16px 0 16px', background: '#ffffff', flexShrink: 0 }}>
                    {headerActions}
                </div>
            )}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ResourceSidebar
                    isEmbedded={true}
                    workers={workers}
                    vehicles={vehicles}
                    categories={categories}
                    kolonnen={kolonnen}
                    activeTab={sidebarMode}
                    onTabChange={onModeChange}
                    assignedIds={assignedIds}
                    isEditMode={isEditMode}
                    onToggleResource={handleToggle}
                    onDragStart={onDragStart}
                    onDoubleClick={(id, type) => {
                        if (type === 'worker') onOpenWorkerModal && onOpenWorkerModal(id, 'absences');
                        else if (type === 'vehicle') navigate('/verwaltung/fuhrpark', { state: { fromLeader: true, openVehicleId: id } });
                        else if (type === 'kolonne') navigate('/verwaltung/mitarbeiter', { state: { targetTab: 'kolonnen', openKolonneId: id } });
                    }}
                    conflictDetection={{
                        getWorkerConflicts,
                        getVehicleConflicts,
                        getKolonneConflicts: (k) => isConflictDetectionEnabled ? getKolonneConflicts(k) : null
                    }}
                    getWorkerStatus={getWorkerStatus}
                    getVehicleStatus={getVehicleStatus}
                    onConflictHover={onConflictHover}
                    onConflictHoverEnd={onConflictHoverEnd}
                    onOpenConflictOverview={onOpenConflictOverview}
                    interaction={interaction}
                    setInteraction={setInteraction}
                />
            </div>
        </div>
    );
}

